import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { GRENER, STORE_LABELS, capitalize } from "../constants/trykkeguide";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function GrenPicker({ selected, onChange }) {
  function toggle(gren) {
    onChange(selected.includes(gren) ? selected.filter((g) => g !== gren) : [...selected, gren]);
  }
  return (
    <div className="flex flex-wrap gap-2">
      {GRENER.map((gren) => (
        <button
          key={gren}
          type="button"
          onClick={() => toggle(gren)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
            selected.includes(gren)
              ? "bg-indigo-600 border-indigo-600 text-white"
              : "bg-white border-gray-300 text-gray-600 hover:border-indigo-300"
          }`}
        >
          {capitalize(gren)}
        </button>
      ))}
    </div>
  );
}

function PrintModal({ url, onClose }) {
  const iframeRef = useRef();

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  function handlePrint() {
    iframeRef.current?.contentWindow?.print();
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">Trykkguide for utskrift</h3>
          <div className="flex gap-2">
            <button onClick={handlePrint} className="px-3 py-1.5 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700">
              Skriv ut
            </button>
            <button onClick={onClose} className="px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200">
              Lukk
            </button>
          </div>
        </div>
        <iframe ref={iframeRef} src={url} title="Trykkguide for utskrift" className="flex-1 w-full border-0" />
      </div>
    </div>
  );
}

function Lightbox({ url, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <img src={url} alt="Trykkeguide" className="max-h-full max-w-full rounded shadow-xl object-contain" onClick={(e) => e.stopPropagation()} />
      <button onClick={onClose} className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full w-8 h-8 flex items-center justify-center text-lg">
        ×
      </button>
    </div>
  );
}

// ── Klubblogo ─────────────────────────────────────────────────────────────────
function LogoUploader({ club, onClubUpdated }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch(`${API_BASE_URL}/trykkeguide/${club._id}/logo`, { method: "POST", body: fd });
      if (!r.ok) throw new Error();
      onClubUpdated(await r.json());
    } catch {
      alert("Kunne ikke laste opp logo.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleRemove() {
    if (!window.confirm("Fjerne klubblogo?")) return;
    try {
      const r = await fetch(`${API_BASE_URL}/trykkeguide/${club._id}/logo`, { method: "DELETE" });
      if (!r.ok) throw new Error();
      onClubUpdated(await r.json());
    } catch {
      alert("Kunne ikke fjerne logo.");
    }
  }

  return (
    <div className="flex flex-col items-center gap-2">
      {club.logo?.url ? (
        <img src={club.logo.url} alt={club.name} className="w-20 h-20 object-contain rounded border border-gray-200 bg-white" />
      ) : (
        <div className="w-20 h-20 rounded border border-gray-200 bg-gray-50 flex items-center justify-center text-3xl text-gray-300">🎽</div>
      )}
      <div className="flex gap-2">
        <label className={`cursor-pointer text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          {uploading ? "…" : club.logo?.url ? "Bytt" : "Last opp"}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
        {club.logo?.url && (
          <button onClick={handleRemove} className="text-xs px-2 py-1 rounded bg-gray-100 text-red-600 hover:bg-gray-200">Fjern</button>
        )}
      </div>
    </div>
  );
}

// ── Klubbinfo (navn/butikk/lenke) ────────────────────────────────────────────
function ClubInfoForm({ club, onClubUpdated }) {
  const [name, setName] = useState(club.name);
  const [butikktilhorighet, setButikktilhorighet] = useState(club.butikktilhorighet);
  const [link, setLink] = useState(club.link || "");
  const [saving, setSaving] = useState(false);
  const dirty = name !== club.name
    || butikktilhorighet !== club.butikktilhorighet
    || link !== (club.link || "");

  async function handleSave() {
    if (!name.trim()) return alert("Klubbnavn er påkrevd.");
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE_URL}/trykkeguide/${club._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, butikktilhorighet, link }),
      });
      if (!r.ok) throw new Error();
      onClubUpdated(await r.json());
    } catch {
      alert("Kunne ikke lagre klubb.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Klubbnavn"
        className="p-2 border border-gray-300 rounded-md md:col-span-1 font-semibold text-lg"
      />
      <select
        value={butikktilhorighet}
        onChange={(e) => setButikktilhorighet(e.target.value)}
        className="p-2 border border-gray-300 rounded-md"
      >
        <option value="ROYKEN">Røyken</option>
        <option value="SLEMMESTAD">Slemmestad</option>
      </select>
      <input
        type="text"
        value={link}
        onChange={(e) => setLink(e.target.value)}
        placeholder="Lenke til nettbutikk"
        className="p-2 border border-gray-300 rounded-md"
      />
      <div className="md:col-span-3 flex items-center gap-3">
        {dirty && (
          <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-60">
            {saving ? "Lagrer…" : "Lagre endringer"}
          </button>
        )}
        {club.link && (
          <a href={club.link} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:text-indigo-800">
            Åpne nettbutikk ↗
          </a>
        )}
      </div>
    </div>
  );
}

// ── Plaggbilder ───────────────────────────────────────────────────────────────
function GarmentImages({ club, garment, onClubUpdated, setLightboxUrl, readOnly }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  if (readOnly) {
    return (
      <div className="mt-3">
        {garment.images.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {garment.images.map((img) => (
              <div key={img._id} className="w-56">
                <img
                  src={img.url}
                  alt={img.caption || garment.name}
                  className="w-56 h-36 object-contain bg-white rounded border cursor-pointer hover:opacity-90"
                  onClick={() => setLightboxUrl(img.url)}
                />
                {img.caption && <p className="mt-1 text-xs text-gray-500">{img.caption}</p>}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">Ingen bilder lagt til ennå.</p>
        )}
      </div>
    );
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch(`${API_BASE_URL}/trykkeguide/${club._id}/garments/${garment._id}/images`, { method: "POST", body: fd });
      if (!r.ok) throw new Error();
      onClubUpdated(await r.json());
    } catch {
      alert("Kunne ikke laste opp bilde.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleCaptionSave(imageId, caption) {
    try {
      const r = await fetch(`${API_BASE_URL}/trykkeguide/${club._id}/garments/${garment._id}/images/${imageId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption }),
      });
      if (!r.ok) throw new Error();
      onClubUpdated(await r.json());
    } catch {
      alert("Kunne ikke lagre bildetekst.");
    }
  }

  async function handleDeleteImage(imageId) {
    if (!window.confirm("Slett dette bildet?")) return;
    try {
      const r = await fetch(`${API_BASE_URL}/trykkeguide/${club._id}/garments/${garment._id}/images/${imageId}`, { method: "DELETE" });
      if (!r.ok) throw new Error();
      onClubUpdated(await r.json());
    } catch {
      alert("Kunne ikke slette bilde.");
    }
  }

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500">Referansebilder ({garment.images.length})</span>
        <label className={`cursor-pointer text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          {uploading ? "Laster opp…" : "+ Legg til bilde"}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
      {garment.images.length > 0 ? (
        <div className="flex flex-wrap gap-3">
          {garment.images.map((img) => (
            <div key={img._id} className="w-56">
              <div className="relative group w-56 h-36 bg-white rounded border">
                <img
                  src={img.url}
                  alt={img.caption || garment.name}
                  className="w-full h-full object-contain cursor-pointer hover:opacity-90"
                  onClick={() => setLightboxUrl(img.url)}
                />
                <button
                  onClick={() => handleDeleteImage(img._id)}
                  className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-600 text-white text-xs leading-none items-center justify-center hidden group-hover:flex"
                  title="Slett bilde"
                >
                  ×
                </button>
              </div>
              <input
                type="text"
                defaultValue={img.caption || ""}
                placeholder="Bildetekst"
                onBlur={(e) => { if (e.target.value !== (img.caption || "")) handleCaptionSave(img._id, e.target.value); }}
                className="mt-1 w-full text-xs p-1 border border-gray-200 rounded"
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-400">Ingen bilder lagt til ennå.</p>
      )}
    </div>
  );
}

// ── Plaggkort (visning) ──────────────────────────────────────────────────────
function GarmentView({ club, garment, setLightboxUrl }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center flex-wrap gap-2">
        <h3 className="font-medium text-gray-900">{garment.name}</h3>
        {garment.grener?.map((gren) => (
          <span key={gren} className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-700">
            {capitalize(gren)}
          </span>
        ))}
      </div>
      {(garment.merke || garment.modell) && (
        <p className="text-xs text-gray-500 mt-0.5">
          {[garment.merke, garment.modell].filter(Boolean).join(" — ")}
        </p>
      )}
      {(garment.veilPris != null || garment.rabatt || garment.trykkPris != null) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
          {garment.veilPris != null && (
            <span className="text-gray-600">Veil. pris: <span className="font-medium text-gray-900">{garment.veilPris} kr</span></span>
          )}
          {!!garment.rabatt && (
            <span className="text-gray-600">Rabatt: <span className="font-medium text-gray-900">{garment.rabatt}%</span></span>
          )}
          {garment.trykkPris != null && (
            <span className="text-gray-600">Trykkpris: <span className="font-medium text-gray-900">{garment.trykkPris} kr</span></span>
          )}
        </div>
      )}
      <GarmentImages club={club} garment={garment} setLightboxUrl={setLightboxUrl} readOnly />
      {garment.instructions && (
        <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{garment.instructions}</p>
      )}
    </div>
  );
}

// ── Plaggkort (redigering) ───────────────────────────────────────────────────
function GarmentCard({ club, garment, onClubUpdated, setLightboxUrl }) {
  const [name, setName] = useState(garment.name);
  const [merke, setMerke] = useState(garment.merke || "");
  const [modell, setModell] = useState(garment.modell || "");
  const [instructions, setInstructions] = useState(garment.instructions || "");
  const [grener, setGrener] = useState(garment.grener || []);
  const [veilPris, setVeilPris] = useState(garment.veilPris ?? "");
  const [rabatt, setRabatt] = useState(garment.rabatt ?? "");
  const [trykkPris, setTrykkPris] = useState(garment.trykkPris ?? "");
  const [saving, setSaving] = useState(false);
  const dirty = name !== garment.name
    || merke !== (garment.merke || "")
    || modell !== (garment.modell || "")
    || instructions !== (garment.instructions || "")
    || JSON.stringify([...grener].sort()) !== JSON.stringify([...(garment.grener || [])].sort())
    || String(veilPris) !== String(garment.veilPris ?? "")
    || String(rabatt) !== String(garment.rabatt ?? "")
    || String(trykkPris) !== String(garment.trykkPris ?? "");

  async function handleSave() {
    if (!name.trim()) return alert("Navn på plagg er påkrevd.");
    if (!window.confirm(`Lagre endringer på "${garment.name}"? Dette påvirker trykkguiden alle ansatte bruker.`)) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE_URL}/trykkeguide/${club._id}/garments/${garment._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, merke, modell, instructions, grener, veilPris, rabatt, trykkPris }),
      });
      if (!r.ok) throw new Error();
      onClubUpdated(await r.json());
    } catch {
      alert("Kunne ikke lagre plagg.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Slette plagget "${garment.name}" fra guiden? Dette kan ikke angres.`)) return;
    try {
      const r = await fetch(`${API_BASE_URL}/trykkeguide/${club._id}/garments/${garment._id}`, { method: "DELETE" });
      if (!r.ok) throw new Error();
      onClubUpdated(await r.json());
    } catch {
      alert("Kunne ikke slette plagg.");
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        <div className="flex flex-col gap-2 md:w-56 shrink-0">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Type tøy, f.eks. T-skjorte"
            className="p-2 border border-gray-300 rounded-md font-medium"
          />
          <input
            type="text"
            value={merke}
            onChange={(e) => setMerke(e.target.value)}
            placeholder="Merke"
            className="p-2 border border-gray-300 rounded-md text-sm"
          />
          <input
            type="text"
            value={modell}
            onChange={(e) => setModell(e.target.value)}
            placeholder="Modell"
            className="p-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Plassering, mål og notater for dette plagget…"
            rows={3}
            className="p-2 border border-gray-300 rounded-md"
          />
          <GrenPicker selected={grener} onChange={setGrener} />
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[11px] text-gray-500">Veil. pris (kr)</label>
              <input
                type="number"
                value={veilPris}
                onChange={(e) => setVeilPris(e.target.value)}
                placeholder="0"
                className="mt-0.5 w-full p-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Rabatt (%)</label>
              <input
                type="number"
                value={rabatt}
                onChange={(e) => setRabatt(e.target.value)}
                placeholder="0"
                className="mt-0.5 w-full p-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Trykkpris (kr)</label>
              <input
                type="number"
                value={trykkPris}
                onChange={(e) => setTrykkPris(e.target.value)}
                placeholder="0"
                className="mt-0.5 w-full p-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>
        </div>
        <div className="flex md:flex-col gap-2 shrink-0">
          {dirty && (
            <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-60">
              {saving ? "Lagrer…" : "Lagre"}
            </button>
          )}
          <button onClick={handleDelete} className="px-3 py-1.5 rounded-md bg-gray-100 text-red-600 text-xs font-medium hover:bg-gray-200">
            Slett plagg
          </button>
        </div>
      </div>
      <GarmentImages club={club} garment={garment} onClubUpdated={onClubUpdated} setLightboxUrl={setLightboxUrl} />
    </div>
  );
}

function NewGarmentForm({ club, onClubUpdated }) {
  const [name, setName] = useState("");
  const [merke, setMerke] = useState("");
  const [modell, setModell] = useState("");
  const [grener, setGrener] = useState([]);
  const [veilPris, setVeilPris] = useState("");
  const [rabatt, setRabatt] = useState("");
  const [trykkPris, setTrykkPris] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return alert("Navn på plagg er påkrevd.");
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE_URL}/trykkeguide/${club._id}/garments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, merke, modell, grener, veilPris, rabatt, trykkPris }),
      });
      if (!r.ok) throw new Error();
      onClubUpdated(await r.json());
      setName("");
      setMerke("");
      setModell("");
      setGrener([]);
      setVeilPris("");
      setRabatt("");
      setTrykkPris("");
    } catch {
      alert("Kunne ikke legge til plagg.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Nytt plagg</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input
          type="text"
          placeholder="Type tøy, f.eks. T-skjorte, Genser, Caps…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="p-2 border border-gray-300 rounded-md"
        />
        <input
          type="text"
          placeholder="Merke (valgfritt)"
          value={merke}
          onChange={(e) => setMerke(e.target.value)}
          className="p-2 border border-gray-300 rounded-md"
        />
        <input
          type="text"
          placeholder="Modell (valgfritt)"
          value={modell}
          onChange={(e) => setModell(e.target.value)}
          className="p-2 border border-gray-300 rounded-md"
        />
      </div>
      <div className="mt-3">
        <p className="text-xs font-medium text-gray-500 mb-1.5">Grener (valgfritt, kan velge flere)</p>
        <GrenPicker selected={grener} onChange={setGrener} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        <input
          type="number"
          placeholder="Veil. pris (kr)"
          value={veilPris}
          onChange={(e) => setVeilPris(e.target.value)}
          className="p-2 border border-gray-300 rounded-md"
        />
        <input
          type="number"
          placeholder="Rabatt (%)"
          value={rabatt}
          onChange={(e) => setRabatt(e.target.value)}
          className="p-2 border border-gray-300 rounded-md"
        />
        <input
          type="number"
          placeholder="Trykkpris (kr)"
          value={trykkPris}
          onChange={(e) => setTrykkPris(e.target.value)}
          className="p-2 border border-gray-300 rounded-md"
        />
      </div>
      <button type="submit" disabled={saving} className="mt-3 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
        + Legg til plagg
      </button>
    </form>
  );
}

function TrykkeguideKlubb() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [activeGrener, setActiveGrener] = useState([]);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  function toggleActiveGren(gren) {
    setActiveGrener((prev) => (prev.includes(gren) ? prev.filter((g) => g !== gren) : [...prev, gren]));
  }

  useEffect(() => {
    async function fetchClub() {
      try {
        const r = await fetch(`${API_BASE_URL}/trykkeguide/${id}`);
        if (!r.ok) throw new Error();
        setClub(await r.json());
      } catch {
        console.error("Kunne ikke hente klubb");
      } finally {
        setLoading(false);
      }
    }
    fetchClub();
  }, [id]);

  if (loading) return <div className="text-center py-10 text-gray-500">Laster inn trykkeguide…</div>;
  if (!club) return <div className="text-center py-10 text-gray-400">Fant ikke klubben.</div>;

  const sortedGarments = [...club.garments].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  const visibleGarments = activeGrener.length > 0
    ? sortedGarments.filter((g) => (g.grener || []).some((gg) => activeGrener.includes(gg)))
    : sortedGarments;
  const printUrl = `${window.location.origin}/trykkeguide/${club._id}/print${activeGrener.length > 0 ? `?gren=${activeGrener.map(encodeURIComponent).join(",")}` : ""}`;

  return (
    <div className="max-w-5xl mx-auto py-8">
      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}

      <button onClick={() => navigate("/trykkeguide")} className="text-sm text-gray-500 hover:text-gray-700 mb-4">
        ← Tilbake til klubbliste
      </button>

      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm mb-6">
        <div className="flex flex-col md:flex-row gap-5">
          <LogoUploader club={club} onClubUpdated={setClub} />
          <ClubInfoForm club={club} onClubUpdated={setClub} />
        </div>
        <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-400">
          <span>Butikktilhørighet: {STORE_LABELS[club.butikktilhorighet] || club.butikktilhorighet}</span>
        </div>

        {sortedGarments.length > 0 && (
          <>
            <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
              <button
                onClick={() => setActiveGrener([])}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  activeGrener.length === 0
                    ? "bg-indigo-600 border-indigo-600 text-white"
                    : "bg-white border-gray-300 text-gray-600 hover:border-indigo-300"
                }`}
              >
                Alle
              </button>
              {GRENER.map((gren) => (
                <button
                  key={gren}
                  onClick={() => toggleActiveGren(gren)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    activeGrener.includes(gren)
                      ? "bg-indigo-600 border-indigo-600 text-white"
                      : "bg-white border-gray-300 text-gray-600 hover:border-indigo-300"
                  }`}
                >
                  {capitalize(gren)}
                </button>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-3">
              <button
                onClick={() => setShowPrintModal(true)}
                className="shrink-0 p-1.5 bg-white border border-gray-200 rounded-md hover:border-indigo-300 transition-colors"
                title="Åpne for visning og utskrift"
              >
                <QRCodeSVG value={printUrl} size={64} level="M" />
              </button>
              <div className="text-xs text-gray-500">
                <p className="font-medium text-gray-700">
                  Skann for trykkguide{activeGrener.length > 0 ? ` — ${activeGrener.map(capitalize).join(", ")}` : ""}
                </p>
                <p>Eller trykk på QR-koden for å åpne og skrive ut.</p>
              </div>
            </div>
          </>
        )}
      </div>

      {showPrintModal && <PrintModal url={printUrl} onClose={() => setShowPrintModal(false)} />}

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-800">Plagg og trykkplassering</h2>
        <button
          onClick={() => setEditMode((v) => !v)}
          className={`text-xs px-3 py-1.5 rounded-md font-medium border transition-colors ${
            editMode
              ? "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
              : "bg-white border-gray-300 text-gray-500 hover:border-indigo-300"
          }`}
        >
          {editMode ? "Lukk redigering" : "🔒 Rediger (kun klubbansvarlig)"}
        </button>
      </div>

      {editMode && (
        <>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
            Endringer her vises umiddelbart for alle ansatte som bruker trykkguiden. Vær sikker før du lagrer eller sletter.
          </p>
          <NewGarmentForm club={club} onClubUpdated={setClub} />
        </>
      )}

      {sortedGarments.length === 0 ? (
        <p className="text-center text-gray-400 py-10">Ingen plagg lagt til i guiden ennå.</p>
      ) : visibleGarments.length === 0 ? (
        <p className="text-center text-gray-400 py-10">Ingen plagg for {activeGrener.map(capitalize).join(", ")} ennå.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {visibleGarments.map((garment) => (
            editMode ? (
              <GarmentCard key={garment._id} club={club} garment={garment} onClubUpdated={setClub} setLightboxUrl={setLightboxUrl} />
            ) : (
              <GarmentView key={garment._id} club={club} garment={garment} setLightboxUrl={setLightboxUrl} />
            )
          ))}
        </div>
      )}
    </div>
  );
}

export default TrykkeguideKlubb;
